import { GraduationCap, PenLine, Music2, Mic } from "lucide-react";

const STEPS = [
    {
        n: "01",
        icon: PenLine,
        emoji: "\u270D\uFE0F",
        title: "Create your song",
        price: "\u00A349.99",
        priceNote: "Journey Begins",
        accent: "#D4A017",
        body: "Working alongside your vocal coach, your ideas begin to come to life.\n\nWhether you\u2019re starting with a single sentence, a notebook full of lyrics, or you\u2019ve always dreamed of recording your own interpretation of a favourite song, we\u2019ll help shape your vision into something that\u2019s uniquely yours.\n\nThis is where imagination becomes reality.\nWhere ideas become lyrics.\nWhere memories become music.\nAnd where, for the very first time, you begin to hear the soundtrack to your own story.\n\nEvery step is guided.\nEvery decision is yours.\n\nBecause this isn\u2019t just another song.\nIt\u2019s your song.",
        bullets: [],
        footer: "",
    },
    {
        n: "02",
        icon: Music2,
        emoji: "\u{1F3B5}",
        title: "Bring it to life",
        price: "\u00A399.99",
        priceNote: "Production stage",
        accent: "#1B4D3E",
        body: "Working alongside your vocal coach, this is where your song truly begins to come to life.\n\nOur production team transforms your ideas into professionally produced music, creating the soundtrack to your story, or chosen song, for the very first time.\n\nYou\u2019ll hear your song take shape through a demonstration version, rehearse with your professionally produced backing track, and build confidence with every lesson as recording day gets closer.\n\nThis is the moment imagination becomes music.\nWhere excitement becomes anticipation.\nAnd where a lifelong dream starts to sound real.\n\nBecause this isn\u2019t just another recording.\nIt\u2019s your song.",
        bullets: [],
        footer: "",
    },
    {
        n: "03",
        icon: Mic,
        emoji: "\u{1F3A4}",
        title: "Record & release",
        price: "\u00A349.99",
        priceNote: "Final stage",
        accent: "#7C2D12",
        body: "Working alongside your vocal coach, you\u2019ll record the vocal performance you\u2019ve been preparing for.\n\nOur production team will then professionally edit, mix and master your song, carefully bringing every element together into a polished, commercial-quality recording ready for release.\n\nThis is the moment everything comes together.\nThe moment your journey becomes your achievement.\nAnd the moment you hear your very own professionally produced song for the very first time.\n\nBecause this isn\u2019t just another recording.\nIt\u2019s your first single.",
        bullets: [],
        footer: "",
    },
];

export default function Lessons() {
    return (
        <section
            id="lessons"
            className="relative px-6 md:px-10 py-16 md:py-24 bg-[#F1E8D5]"
            data-testid="lessons-section"
        >
            <div className="max-w-7xl mx-auto">
                <div className="reveal mb-8 md:mb-12 max-w-3xl">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="w-11 h-11 rounded-2xl bg-[#1B4D3E] text-white border-2 border-black flex items-center justify-center">
                            <GraduationCap size={20} />
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-widest font-bold text-stone-500">
                            Your trusted relationship
                        </span>
                    </div>
                    <h2 className="font-display tracking-tighter font-black leading-[0.95] text-5xl md:text-6xl lg:text-7xl text-black">
                        Your{" "}
                        <em className="not-italic bg-[#1B4D3E] text-white px-3 -rotate-1 inline-block">singing</em>{" "}
                        lessons.
                    </h2>
                </div>

                <div className="reveal">
                    {/* Main copy — full width */}
                    <div className="space-y-5 text-stone-800 text-base md:text-lg leading-relaxed">
                        <p>
                            Your My First Single vocal coach is a specially
                            selected, local independent vocal coach who
                            will guide you throughout your entire journey.
                        </p>
                        <p>
                            The cost of your singing lessons is agreed{" "}
                            <span className="font-bold">directly with them</span>
                            , allowing you to choose a lesson schedule and pace
                            that suits your individual goals, availability and
                            budget.
                        </p>
                        <p>
                            Your vocal coach will help you prepare for each stage of
                            the programme, ensuring you&rsquo;re confident and
                            ready before moving on to the next step. Together,
                            you&rsquo;ll decide when it&rsquo;s the right time
                            to progress from developing your song, to
                            rehearsing, recording and finally releasing your
                            finished single.
                        </p>
                        <p>
                            For some, that song will tell a deeply personal
                            story. For others, it will be a fresh
                            interpretation of a favourite they&rsquo;ve always
                            dreamed of recording. Whichever path you choose,
                            the destination is the same &mdash; helping you
                            achieve something you&rsquo;ll remember for the
                            rest of your life.
                        </p>
                        <p>
                            This personal approach means every journey is
                            unique, with your vocal coach supporting you
                            every step of the way while tailoring lessons to
                            your individual needs.
                        </p>
                    </div>
                </div>

                {/* Steps grid — full width under the singing lessons area */}
                <div className="mt-8 md:mt-12 grid md:grid-cols-3 gap-6 md:gap-7 reveal">
                    {STEPS.map((s, i) => (
                        <article
                            key={s.n}
                            className="relative bg-white text-black rounded-3xl p-7 md:p-8 flex flex-col border-2 border-black shadow-[6px_6px_0_0_#0A0A0A] hover:shadow-[3px_3px_0_0_#0A0A0A] hover:translate-x-[3px] hover:translate-y-[3px] transition-all overflow-hidden"
                            style={{ transitionDelay: `${i * 60}ms` }}
                            data-testid={`lessons-step-${i + 1}`}
                        >
                            <div
                                className="absolute top-0 left-0 right-0 h-1.5"
                                style={{ background: s.accent }}
                            />
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <span
                                        className="w-11 h-11 rounded-2xl border-2 border-black flex items-center justify-center text-white"
                                        style={{ background: s.accent }}
                                    >
                                        <s.icon size={20} />
                                    </span>
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-stone-500">
                                            Step {s.n}
                                        </div>
                                        <h3 className="font-display font-black tracking-tighter text-2xl md:text-3xl leading-[1] mt-0.5">
                                            <span className="mr-1.5" aria-hidden>{s.emoji}</span>
                                            {s.title}
                                        </h3>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div
                                        className="font-display font-black tracking-tighter text-2xl md:text-3xl leading-none"
                                        style={{ color: s.accent }}
                                    >
                                        {s.price}
                                    </div>
                                    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest font-bold text-stone-500">
                                        {s.priceNote}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="my-6 h-0.5"
                                style={{ background: s.accent, opacity: 0.3 }}
                            />
                            <p className="text-stone-700 text-[15px] leading-relaxed whitespace-pre-line">
                                {s.body}
                            </p>
                            {s.bullets.length > 0 && (
                                <ul className="mt-4 space-y-2">
                                    {s.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-3 text-[15px] text-stone-800"
                                        >
                                            <span
                                                className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                style={{ background: s.accent }}
                                            />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {s.footer && (
                                <p className="mt-5 text-stone-800 text-[15px] font-medium">
                                    {s.footer}
                                </p>
                            )}
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
